import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import {
  FORMULES_CATALOGUE,
  type FormuleCatalogue,
  type FormuleId,
  type FormuleLimites,
  type FormuleStatut,
} from "@/config/formules";

export interface FormuleUpdate {
  nom?: string;
  tagline?: string;
  prixMensuel?: number;
  prixAnnuel?: number;
  cta?: string;
  limites?: Partial<FormuleLimites>;
  statut?: FormuleStatut;
}

interface FormulesContextValue {
  formules: FormuleCatalogue[];
  getFormule: (id: string) => FormuleCatalogue | undefined;
  updateFormule: (id: FormuleId, patch: FormuleUpdate) => void;
  toggleStatut: (id: FormuleId) => void;
}

const FormulesContext = createContext<FormulesContextValue | null>(null);

type Row = Record<string, unknown>;
const num = (value: unknown): number => (typeof value === "number" ? value : Number(value ?? 0) || 0);
const numOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Détecte un échec Réseau (le navigateur n'a pas pu joindre Supabase :
 * coupure, DNS, blocage temporaire…). Ce n'est PAS une erreur métier ni RLS :
 * ces cas méritent un réessai automatique avant tout repli.
 */
function isNetworkError(message: string): boolean {
  return /failed to fetch|networkerror|network error|load failed|fetch failed|err_network|err_internet|net::|timed? ?out/i.test(
    message
  );
}

const LIMITE_KEYS: Array<keyof FormuleLimites> = [
  "proprietaires",
  "immeubles",
  "biens",
  "locataires",
  "contrats",
  "employes",
];

/** Base initiale : source de vérité validée `@/config/formules`. */
function initialFormules(): FormuleCatalogue[] {
  return FORMULES_CATALOGUE.map((formule) => ({ ...formule, limites: { ...formule.limites } }));
}

const FORMULE_CODES: FormuleId[] = ["gratuit", "essentiel", "professionnel", "agence"];

function isFormuleCode(value: string): value is FormuleId {
  return (FORMULE_CODES as string[]).includes(value);
}

/**
 * Ordre d'affichage officiel des formules : Gratuit → Essentiel → Professionnel → Agence.
 * On NE se fie PAS à l'ordre renvoyé par Supabase (aucun `ORDER BY` garanti),
 * sinon Agence peut apparaître au milieu. Ce tri est purement visuel :
 * il ne modifie ni les prix, ni les limites, ni aucune donnée en base.
 */
function ordonnerFormules(formules: FormuleCatalogue[]): FormuleCatalogue[] {
  return [...formules].sort(
    (a, b) => FORMULE_CODES.indexOf(a.id) - FORMULE_CODES.indexOf(b.id)
  );
}

/** Convertit une ligne réelle de `formules` en entrée de catalogue (fusionnée avec la config UI). */
function rowToFormule(row: Row): FormuleCatalogue | null {
  const code = typeof row.code === "string" ? row.code : "";
  if (!isFormuleCode(code)) return null;
  const base = FORMULES_CATALOGUE.find((formule) => formule.id === code);
  if (!base) return null;
  const rawLimites = (row.limites ?? {}) as Record<string, unknown>;
  const limites = { ...base.limites };
  LIMITE_KEYS.forEach((key) => {
    if (key in rawLimites) limites[key] = numOrNull(rawLimites[key]);
  });
  return {
    id: code,
    nom: typeof row.nom === "string" && row.nom.trim() ? row.nom : base.nom,
    tagline: base.tagline,
    prixMensuel: num(row.prix_mensuel),
    prixAnnuel: num(row.prix_annuel),
    highlight: base.highlight,
    cta: base.cta,
    statut: row.actif === false ? "desactivee" : "active",
    limites,
  };
}

/**
 * Formules (Super Admin) — branché sur la table RÉELLE `formules`.
 *
 * - Lecture : prix, limites et activation proviennent de Supabase.
 * - Écriture : `updateFormule` / `toggleStatut` mettent réellement à jour la
 *   table `formules` (RLS `formules_update_admin`), avec repli sûr en cas d'échec.
 *
 * Aucune donnée fictive : si la base ne renvoie rien, on retombe sur la source
 * de vérité validée sans inventer de nouvelles valeurs.
 */
export function FormulesProvider({ children }: { children: ReactNode }) {
  const [formules, setFormules] = useState<FormuleCatalogue[]>(initialFormules);

  const recharger = useCallback(async () => {
    // Réessai léger en cas d'échec RÉSEAU (transitoire) : on tente jusqu'à 3 fois
    // avec un backoff croissant avant de retomber sur la configuration locale.
    const maxTentatives = 3;
    for (let tentative = 1; tentative <= maxTentatives; tentative += 1) {
      try {
        // Lecture de la table RÉELLE `formules`. La policy RLS `formules_select_anon`
        // autorise déjà les visiteurs anonymes à lire les formules actives.
        const { data, error } = await supabase
          .from("formules")
          .select("id, code, nom, prix_mensuel, prix_annuel, actif, limites");
        if (error) throw error;
        const mapped = ((data as Row[] | null) ?? [])
          .map(rowToFormule)
          .filter((formule): formule is FormuleCatalogue => formule !== null);
        if (mapped.length > 0) setFormules(ordonnerFormules(mapped));
        return;
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : String(caught);
        // `permission denied` (42501) : le rôle `anon` n'a pas encore le GRANT SELECT.
        // Les valeurs locales étant strictement identiques, aucun message n'est affiché.
        if (/permission denied|42501/i.test(message)) return;

        // Échec réseau : on retente tant qu'il reste des tentatives.
        if (isNetworkError(message) && tentative < maxTentatives) {
          await new Promise((resolve) => {
            setTimeout(resolve, 400 * tentative);
          });
          continue;
        }

        // Échec persistant : simple avertissement (pas une erreur rouge), car le
        // repli sur la configuration locale validée est sans impact fonctionnel.
        console.warn(
          "[Formules] chargement Supabase indisponible — repli sur la configuration locale.",
          message
        );
        return;
      }
    }
  }, []);

  useEffect(() => {
    void recharger();
    // Relance la lecture dès qu'une session devient disponible (connexion), pour
    // que le Super Admin voie immédiatement les valeurs réellement en base.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setTimeout(() => {
          void recharger();
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [recharger]);

  const getFormule = useCallback((id: string) => formules.find((formule) => formule.id === id), [formules]);

  const updateFormule = useCallback(
    (id: FormuleId, patch: FormuleUpdate) => {
      const previous = formules.find((formule) => formule.id === id);
      setFormules((prev) =>
        prev.map((formule) =>
          formule.id === id
            ? {
                ...formule,
                ...patch,
                limites: patch.limites ? { ...formule.limites, ...patch.limites } : formule.limites,
              }
            : formule
        )
      );

      const update: Record<string, unknown> = {};
      if (patch.nom !== undefined) update.nom = patch.nom;
      if (patch.prixMensuel !== undefined) update.prix_mensuel = patch.prixMensuel;
      if (patch.prixAnnuel !== undefined) update.prix_annuel = patch.prixAnnuel;
      if (patch.statut !== undefined) update.actif = patch.statut === "active";
      if (patch.limites && previous) {
        update.limites = { ...previous.limites, ...patch.limites };
      }
      if (Object.keys(update).length === 0) return;

      void supabase
        .from("formules")
        .update(update)
        .eq("code", id)
        .then(({ error }) => {
          if (error) {
            if (previous) setFormules((prev) => prev.map((formule) => (formule.id === id ? previous : formule)));
            dispatchDataError({
              scope: "Formules",
              message: "La modification de la formule a échoué.",
              technical: describeSupabaseError(error),
            });
          }
        });
    },
    [formules]
  );

  const toggleStatut = useCallback(
    (id: FormuleId) => {
      const current = formules.find((formule) => formule.id === id);
      if (!current) return;
      const statut: FormuleStatut = current.statut === "active" ? "desactivee" : "active";
      setFormules((prev) => prev.map((formule) => (formule.id === id ? { ...formule, statut } : formule)));
      void supabase
        .from("formules")
        .update({ actif: statut === "active" })
        .eq("code", id)
        .then(({ error }) => {
          if (error) {
            setFormules((prev) => prev.map((formule) => (formule.id === id ? { ...formule, statut: current.statut } : formule)));
            dispatchDataError({
              scope: "Formules",
              message: "Le changement de statut de la formule a échoué.",
              technical: describeSupabaseError(error),
            });
          }
        });
    },
    [formules]
  );

  const value = useMemo(
    () => ({ formules, getFormule, updateFormule, toggleStatut }),
    [formules, getFormule, updateFormule, toggleStatut]
  );

  return <FormulesContext.Provider value={value}>{children}</FormulesContext.Provider>;
}

export function useFormules(): FormulesContextValue {
  const context = useContext(FormulesContext);
  if (!context) {
    throw new Error("useFormules doit être utilisé à l'intérieur de FormulesProvider");
  }
  return context;
}